import { cn } from "@/lib/utils";
import { Slider, type SliderRoot } from "@base-ui/react/slider";
import { RotateCcw } from "lucide-react";
import { useId } from "react";
import Description from "../description";
import FieldError from "../field-error";
import Label from "../label";
import "./style.scss";

export interface RangeSliderProps extends Omit<React.ComponentProps<typeof Slider.Root>, "children"> {
	label?: string;
	description?: string | null;
	error?: string;
	containerClassName?: string;
	required?: boolean;
	resetButton?: boolean;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
	label,
	description,
	error,
	containerClassName,
	required,
	orientation = "horizontal",
	thumbAlignment = "edge",
	resetButton = true,
	value: valueProp,
	defaultValue: defaultValueProp,
	min = 0,
	max = 100,
	...props
}) => {
	const generatedId = useId();
	const id = props.id ?? generatedId;

	// TODO: the value should always be an array in the slider, that would remove the need for the array checks
	const currentValue = valueProp ?? defaultValueProp ?? min;
	const thumbValues = Array.isArray(currentValue) ? currentValue : [currentValue];
	const resetValue = defaultValueProp ?? min;
	const resetValues = Array.isArray(resetValue) ? resetValue : [resetValue];
	const valueChanged = thumbValues.length !== resetValues.length || thumbValues.some((value, index) => value !== resetValues[index]);

	const handleReset = (event: React.MouseEvent<HTMLButtonElement>) => {
		props.onValueChange?.(resetValue, {
			reason: "none",
			event: event.nativeEvent,
			cancel: () => {},
			allowPropagation: () => {},
			isCanceled: false,
			isPropagationAllowed: false,
			trigger: event.currentTarget,
			activeThumbIndex: 0,
		} satisfies SliderRoot.ChangeEventDetails);
	};

	const showMinLabel = (thumbValues[0] ?? min) !== min;
	const showMaxLabel = (thumbValues[thumbValues.length - 1] ?? max) !== max;

	return (
		<Slider.Root
			{...props}
			id={id}
			orientation={orientation}
			thumbAlignment={thumbAlignment}
			min={min}
			max={max}
			className={cn("flex flex-col items-start gap-2", containerClassName)}
			value={valueProp}
			defaultValue={valueProp === undefined ? defaultValueProp : undefined}
		>
			{label && (
				<Label variant="slider" required={required}>
					<div className="flex items-center gap-2">
						<span>{label}</span>

						{resetButton && valueChanged && (
							<button type="button" onClick={handleReset} className="cursor-pointer transition hover:scale-110">
								<RotateCcw size={14} />
							</button>
						)}
					</div>
				</Label>
			)}

			<div className={cn("w-full flex items-center gap-3 pt-7", orientation === "vertical" && "h-40 w-auto flex-col")}>
				<Slider.Control
					className={cn(
						"ms-range-slider__control relative flex",
						orientation === "horizontal" ? "h-5 w-full items-center" : "h-full w-5 justify-center",
					)}
				>
					{orientation === "horizontal" && (showMinLabel || showMaxLabel) && (
						<div className="pointer-events-none absolute -top-7 left-0 right-0 h-0">
							{showMinLabel && (
								<span
									className={cn(
										"absolute left-0 text-xs leading-none whitespace-nowrap rounded-full border px-2 py-1 shadow-xs opacity-45",
										"bg-(--ms-form-el-checkbox-background-color,var(--ms-form-el-background-color))",
										"border-(--ms-form-el-checkbox-border-color,var(--ms-form-el-border-color))",
										"text-(--ms-form-el-checkbox-accent-color,var(--ms-form-el-color))",
									)}
								>
									{min.toLocaleString("nl-NL")}
								</span>
							)}
							{showMaxLabel && (
								<span
									className={cn(
										"absolute right-0 text-xs leading-none whitespace-nowrap rounded-full border px-2 py-1 shadow-xs opacity-45",
										"bg-(--ms-form-el-checkbox-background-color,var(--ms-form-el-background-color))",
										"border-(--ms-form-el-checkbox-border-color,var(--ms-form-el-border-color))",
										"text-(--ms-form-el-checkbox-accent-color,var(--ms-form-el-color))",
									)}
								>
									{max.toLocaleString("nl-NL")}
								</span>
							)}
						</div>
					)}

					<Slider.Track
						className={cn(
							"ms-range-slider__track relative overflow-hidden rounded-full w-full",
							orientation === "horizontal" ? "h-2 w-full" : "h-full w-2",
						)}
					>
						<Slider.Indicator className="ms-range-slider__indicator absolute rounded-full data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full" />
					</Slider.Track>

					{thumbValues.map((thumbValue, index) => (
						<Slider.Thumb
							key={index}
							index={index}
							className="ms-range-slider__thumb transition relative block size-5 rounded-full border focus-visible:outline-2 focus-visible:outline-offset-2"
						>
							<span
								className={cn(
									"absolute -top-7 text-xs leading-none whitespace-nowrap rounded-full border px-2 py-1 shadow-xs select-none",
									"bg-(--ms-form-el-checkbox-background-color,var(--ms-form-el-background-color))",
									"border-(--ms-form-el-checkbox-border-color,var(--ms-form-el-border-color))",
									"text-(--ms-form-el-checkbox-accent-color,var(--ms-form-el-color))",
									thumbValues.length === 1 && "left-1/2 -translate-x-1/2",
									thumbValues.length > 1 && index === 0 && "left-0",
									index > 0 && index < thumbValues.length - 1 && "left-1/2 -translate-x-1/2",
									thumbValues.length > 1 && index === thumbValues.length - 1 && "right-0",
								)}
							>
								{thumbValue.toLocaleString("nl-NL")}
							</span>
						</Slider.Thumb>
					))}
				</Slider.Control>
			</div>

			{description && <Description>{description}</Description>}

			<FieldError message={error} />
		</Slider.Root>
	);
};

export default RangeSlider;
